import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Building2, Download, Flame, HeartPulse, Loader2, Pencil, Plus, Save,
  ShieldCheck, Users, X,
} from "lucide-react";

/**
 * L'organigramma.
 *
 * Le caselle non si scrivono: si riempiono da sole. Il datore di lavoro viene
 * dalle impostazioni delle nomine, gli addetti antincendio e al primo soccorso
 * dagli attestati in corso di validita', le squadre dalle mansioni in
 * anagrafica. Cosi' quando un attestato scade la casella cambia da sola, che
 * e' esattamente quello che un organigramma disegnato in Word non fa.
 *
 * A mano restano solo gli incarichi che nessun attestato dichiara - RSPP,
 * responsabile del sistema, funzione di conformita' - e si scelgono da un
 * menu, una volta.
 */

async function scaricaPdf() {
  try {
    const { data, headers } = await api.get("/sgi/organigramma/pdf",
                                            { responseType: "blob" });
    const proposto = (headers["content-disposition"] || "").split("filename=")[1];
    const nome = proposto ? proposto.split('"').join("").trim() : "organigramma.pdf";
    const url = URL.createObjectURL(new Blob([data]));
    const a = document.createElement("a");
    a.href = url; a.download = nome;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Errore nella generazione del PDF");
  }
}

export default function Organigramma({ puoScrivere }) {
  const [d, setD] = useState(null);
  const [carico, setCarico] = useState(true);
  const [modifica, setModifica] = useState(false);
  const [bozza, setBozza] = useState({});
  const [salvo, setSalvo] = useState(false);

  const carica = useCallback(async () => {
    setCarico(true);
    try {
      const { data } = await api.get("/sgi/organigramma");
      setD(data);
      const b = {};
      for (const i of data.incarichi) {
        b[i.codice] = i.multiplo
          ? { persone: i.persone || [], nomi: i.esterni || [] }
          : { persona_id: i.persona_id || "", nome: i.esterno_nome || "" };
      }
      setBozza(b);
    } catch {
      toast.error("Errore nel caricamento dell'organigramma");
    } finally { setCarico(false); }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const salva = async () => {
    setSalvo(true);
    try {
      const { data } = await api.put("/sgi/organigramma", { incarichi: bozza });
      setD(data);
      setModifica(false);
      toast.success("Organigramma aggiornato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSalvo(false); }
  };

  // Gli incarichi che ne ammettono piu' d'uno - i referenti dei cantieri -
  // si riempiono scegliendo una persona per volta: resta l'elenco di quelli
  // gia' scelti, con la crocetta per toglierne uno.
  const aggiungiPersona = (codice, pid) => {
    if (!pid) return;
    const ora = bozza[codice]?.persone || [];
    if (ora.includes(pid)) return;
    setBozza({ ...bozza, [codice]: { ...bozza[codice], persone: [...ora, pid] } });
  };

  const togliPersona = (codice, pid) => {
    const ora = bozza[codice]?.persone || [];
    setBozza({ ...bozza,
               [codice]: { ...bozza[codice], persone: ora.filter((x) => x !== pid) } });
  };

  if (carico || !d) {
    return (
      <p className="text-sm text-slate-500 py-10 text-center flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
      </p>
    );
  }

  const perCodice = Object.fromEntries(d.incarichi.map((i) => [i.codice, i]));
  const nomeDi = (codice) => perCodice[codice]?.nome_persona;
  const perSquadra = Object.fromEntries(d.squadre.map((s) => [s.codice, s]));

  const secondo = [
    { titolo: "RSPP", nome: nomeDi("rspp"), nota: "art. 17 c. 1 lett. b" },
    { titolo: "Medico competente", nome: d.azienda.medico_competente,
      nota: "art. 18 c. 1 lett. a" },
    { titolo: "RLS", nota: "eletto dai lavoratori · art. 47",
      nome: d.azienda.rls_dichiarato || d.figure.rls[0]?.nome },
    { titolo: "Responsabile SGI", nome: nomeDi("res_sgi"), nota: "Manuale del sistema" },
    { titolo: "Funzione di conformità", nome: nomeDi("funzione_conformita"),
      nota: "ISO 37001 · 5.3.2" },
  ];

  const terzo = [
    { titolo: "Direzione tecnica", nome: nomeDi("res_tecnico"), squadra: "tecnici" },
    { titolo: "Amministrazione", nome: nomeDi("res_amministrazione"),
      squadra: "amministrazione" },
    { titolo: "Magazzino e DPI", nome: nomeDi("res_magazzino"), squadra: "magazzino" },
    { titolo: "Cantieri", nome: nomeDi("res_cantieri"), squadra: "preposti",
      piu: "operatori" },
  ];

  return (
    <div className="space-y-5" data-testid="organigramma">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-black tracking-tight">Organigramma</h2>
          <p className="text-sm text-slate-500 max-w-2xl">
            Non si disegna: si riempie dai dati dell&apos;app. Le figure delle emergenze
            e i preposti arrivano dagli attestati in corso di validità, quindi la
            casella cambia da sola quando un attestato viene rinnovato o scade.
          </p>
        </div>
        <div className="flex gap-2">
          {puoScrivere && !modifica && (
            <Button variant="outline" onClick={() => setModifica(true)} className="h-11">
              <Pencil className="w-4 h-4 mr-2" /> Assegna gli incarichi
            </Button>
          )}
          <Button onClick={scaricaPdf}
                  className="bg-orange-500 hover:bg-orange-600 text-white h-11">
            <Download className="w-4 h-4 mr-2" /> Scarica il PDF
          </Button>
        </div>
      </div>

      {d.da_assegnare.length > 0 && !modifica && (
        <div className="text-sm border border-orange-200 bg-orange-50 rounded-lg px-3 py-2 text-orange-900">
          Ancora da assegnare: {d.da_assegnare.join(", ")}.
        </div>
      )}

      {modifica ? (
        <Card>
          <CardContent className="p-4 space-y-4">
            <p className="text-sm text-slate-500">
              Solo gli incarichi che nessun attestato può dichiarare. Chi non è un
              dipendente — un RSPP esterno, un consulente — si scrive a mano.
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              {d.incarichi.map((i) => (
                <div key={i.codice} className="border border-slate-200 rounded-lg p-3">
                  <Label className="text-xs uppercase tracking-wide text-slate-500">
                    {i.nome}
                  </Label>
                  <p className="text-xs text-slate-400 mb-2">{i.esteso}</p>
                  {i.multiplo ? (<>
                    {(bozza[i.codice]?.persone || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {(bozza[i.codice]?.persone || []).map((pid) => (
                          <span key={pid}
                                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 pl-2.5 pr-1 py-1 text-xs">
                            {d.persone.find((p) => p.id === pid)?.nome || pid}
                            <button type="button" onClick={() => togliPersona(i.codice, pid)}
                                    title="Togli dall'incarico"
                                    className="p-0.5 text-slate-400 hover:text-red-600">
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <select value=""
                            data-testid={`incarico-${i.codice}`}
                            onChange={(e) => aggiungiPersona(i.codice, e.target.value)}
                            className="w-full h-11 border border-slate-200 rounded-lg px-3 text-sm bg-white">
                      <option value="">
                        {(bozza[i.codice]?.persone || []).length
                          ? "— aggiungine un altro —" : "— nessuno assegnato —"}
                      </option>
                      {d.persone
                        .filter((p) => !(bozza[i.codice]?.persone || []).includes(p.id))
                        .map((p) => (
                          <option key={p.id} value={p.id}>{p.nome}</option>
                        ))}
                    </select>
                    <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Se ne possono mettere più d&apos;uno.
                    </p>
                  </>) : (<>
                  <select value={bozza[i.codice]?.persona_id || ""}
                          data-testid={`incarico-${i.codice}`}
                          onChange={(e) => setBozza({
                            ...bozza,
                            [i.codice]: { persona_id: e.target.value, nome: "" },
                          })}
                          className="w-full h-11 border border-slate-200 rounded-lg px-3 text-sm bg-white">
                    <option value="">
                      {i.esterno ? "— esterno o non assegnato —" : "— non assegnato —"}
                    </option>
                    {d.persone.map((p) => (
                      <option key={p.id} value={p.id}>{p.nome}</option>
                    ))}
                  </select>
                  {i.esterno && !bozza[i.codice]?.persona_id && (
                    <Input value={bozza[i.codice]?.nome || ""}
                           placeholder="Nome e cognome, se è esterno"
                           className="h-11 mt-2"
                           onChange={(e) => setBozza({
                             ...bozza,
                             [i.codice]: { persona_id: "", nome: e.target.value },
                           })} />
                  )}
                  </>)}
                  <p className="text-[11px] text-slate-400 mt-1.5">{i.riferimento}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button onClick={salva} disabled={salvo}
                      data-testid="salva-organigramma"
                      className="bg-orange-500 hover:bg-orange-600 text-white h-11">
                {salvo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                       : <Save className="w-4 h-4 mr-2" />}
                Salva
              </Button>
              <Button variant="outline" onClick={() => { setModifica(false); carica(); }}
                      className="h-11">
                Annulla
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-5 overflow-x-auto">
            <div className="min-w-[720px] space-y-5">
              <Casella titolo="Direzione" nome={d.azienda.datore_lavoro}
                       nota="Datore di lavoro · D.Lgs. 81/2008 art. 2 c. 1 lett. b"
                       principale />

              <Ramo />

              <div className="grid grid-cols-5 gap-2">
                {secondo.map((x) => (
                  <Casella key={x.titolo} titolo={x.titolo} nome={x.nome} nota={x.nota} />
                ))}
              </div>

              <Ramo />

              <div className="grid grid-cols-4 gap-2">
                {terzo.map((x) => {
                  const s = perSquadra[x.squadra];
                  const piu = x.piu ? perSquadra[x.piu]?.quanti || 0 : 0;
                  const quanti = (s?.quanti || 0) + piu;
                  return (
                    <Casella key={x.titolo} titolo={x.titolo} nome={x.nome} chiara
                             nota={`${quanti} ${quanti === 1 ? "persona" : "persone"}`} />
                  );
                })}
              </div>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <Fascia titolo="Addetti antincendio" icona={Flame} colore="red"
                    gente={d.figure.antincendio} />
            <Fascia titolo="Addetti al primo soccorso" icona={HeartPulse} colore="emerald"
                    gente={d.figure.primo_soccorso} />
            <Fascia titolo="Preposti incaricati" icona={ShieldCheck} colore="orange"
                    gente={d.figure.preposto} />
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {d.squadre.filter((s) => s.quanti > 0).map((s) => (
              <Card key={s.codice}>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-sm flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-[#0067b2]" /> {s.nome}
                    <span className="text-slate-400 font-normal">{s.quanti}</span>
                  </h3>
                  <div className="space-y-1">
                    {s.membri.map((m) => (
                      <div key={m.id} className="flex items-center justify-between gap-2 text-sm">
                        <span className="truncate">{m.nome}</span>
                        <span className="text-xs text-slate-400 truncate">{m.mansione}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-xs text-slate-400 flex items-center gap-2">
            <Building2 className="w-3.5 h-3.5" />
            {d.azienda.ragione_sociale} · {d.azienda.sede} ·{" "}
            {d.azienda.totale_addetti} addetti in forza
          </p>
        </div>
      )}
    </div>
  );
}

function Ramo() {
  return (
    <div className="flex justify-center">
      <div className="w-px h-5 bg-slate-300" />
    </div>
  );
}

function Casella({ titolo, nome, nota, principale, chiara }) {
  return (
    <div className={`rounded-lg border-2 p-3 text-center ${
      principale ? "border-orange-400 bg-orange-50"
                 : chiara ? "border-slate-200 bg-slate-50"
                          : "border-[#0067b2] bg-white"}`}>
      <div className={`text-[10px] uppercase tracking-widest font-semibold ${
        principale ? "text-orange-700" : "text-[#0067b2]"}`}>
        {titolo}
      </div>
      <div className={`font-semibold leading-tight mt-1 ${
        principale ? "text-lg" : "text-sm"} ${nome ? "" : "text-slate-400 italic font-normal"}`}>
        {nome || "da assegnare"}
      </div>
      {nota && <div className="text-[10px] text-slate-400 mt-1 leading-tight">{nota}</div>}
    </div>
  );
}

function Fascia({ titolo, icona: Icona, colore, gente }) {
  const tinte = {
    red: "border-red-200 bg-red-50 text-red-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    orange: "border-orange-200 bg-orange-50 text-orange-700",
  };
  return (
    <div className={`rounded-lg border p-3 ${tinte[colore]}`}>
      <div className="text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5">
        <Icona className="w-3.5 h-3.5" /> {titolo}
        <span className="ml-auto tabular-nums">{gente.length}</span>
      </div>
      <div className="mt-2 space-y-0.5">
        {gente.length === 0 && (
          <p className="text-xs text-slate-500 italic">
            Nessuno con attestato in corso di validità.
          </p>
        )}
        {gente.map((g) => (
          <div key={g.id} className="text-sm text-slate-800 truncate">{g.nome}</div>
        ))}
      </div>
    </div>
  );
}
